import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { AlertService } from '../services/AlertService';
import { ProcessingDetailsPanel } from '../components/ProcessingDetailsPanel';
import { TransactionFilters } from '../components/TransactionFilters';
import { LoadingScreen } from '../components/LoadingScreen';
import { Pagination } from '../components/Pagination';
import { AssignmentAPI, TransactionAPI } from '../services/api';
import { UnifiedTransaction } from '../types/payment';
import { useTransactionFilters } from '../hooks/useTransactionFilters';
import './MyAssignedProcessing.css';

// Sales marked as Deposit in the processing panel. The server scopes the list:
// processor agent = sales assigned to them, processor TL = their team's,
// floor manager and above = all.
const fmtDateTime = (v?: string | null): string =>
  v ? `${new Date(v).toLocaleDateString()} ${new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'N/A';

const Deposits: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<UnifiedTransaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: keyof UnifiedTransaction; direction: 'asc' | 'desc' }>({
    field: 'deposit_marked_at',
    direction: 'desc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const { filters, filteredTransactions, updateFilter, clearFilters, hasActiveFilters, filterStats } =
    useTransactionFilters(transactions);

  const loadDeposits = useCallback(async () => {
    try {
      setLoading(true);
      setTransactions(await AssignmentAPI.getDepositSales());
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to load deposits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDeposits(); }, [loadDeposits]);

  const handleCloseDetails = () => {
    setSelectedTransaction(null);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleDecryptData = async (transactionId: string, reason: string) => {
    try {
      const response = await TransactionAPI.decryptTransactionData(transactionId, reason);
      setDecryptedData(response);
      setShowEncryptedData(transactionId);
      AlertService.success('Transaction data decrypted successfully');
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to decrypt data');
    }
  };

  const handleSort = (field: keyof UnifiedTransaction) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'desc',
    }));
  };
  const sortMark = (field: keyof UnifiedTransaction) =>
    sortConfig.field === field && <span className="sort-indicator">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>;

  const pageRows = React.useMemo(() => {
    const rows = [...filteredTransactions];
    rows.sort((a, b) => {
      const av = a[sortConfig.field];
      const bv = b[sortConfig.field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const c = av < bv ? -1 : av > bv ? 1 : 0;
      return sortConfig.direction === 'asc' ? c : -c;
    });
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [filteredTransactions, sortConfig, currentPage, itemsPerPage]);

  if (loading && transactions.length === 0) {
    return <LoadingScreen isVisible={true} message="Loading deposits..." />;
  }

  const scopeText = user?.role === 'processor_agent'
    ? 'Sales assigned to you that are marked as Deposit'
    : user?.role === 'processor_team_lead'
      ? "Sales marked as Deposit for you and your team"
      : 'All sales marked as Deposit';

  return (
    <DashboardLayout>
      <div className="my-assigned-processing">
        <div className="page-header">
          <div>
            <h1>Deposits</h1>
            <p>{scopeText}</p>
          </div>
        </div>

        <TransactionFilters
          filters={filters}
          updateFilter={updateFilter}
          clearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          transactions={transactions}
          filterStats={filterStats}
          showProcessingStatusFilter={true}
          showSaleFilters={true}
        />

        {hasActiveFilters && (
          <div className="filter-summary">
            <span>Showing {filterStats.filtered} of {filterStats.total} deposits</span>
            <button className="clear-filters-btn" onClick={clearFilters}>Clear All Filters</button>
          </div>
        )}

        <div className="transactions-section">
          <div className="section-header">
            <h2>Deposits ({filteredTransactions.length})</h2>
            <button className="btn btn-outline-primary" onClick={loadDeposits} disabled={loading}>
              {loading ? 'Loading...' : '🔄 Refresh'}
            </button>
          </div>

          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('first_name')}>Customer{sortMark('first_name')}</th>
                  <th className="sortable" onClick={() => handleSort('amount')}>Amount{sortMark('amount')}</th>
                  <th className="sortable" onClick={() => handleSort('created_at')}>Sale Date{sortMark('created_at')}</th>
                  <th>Category</th>
                  <th>State</th>
                  <th className="sortable" onClick={() => handleSort('processing_status')}>Status{sortMark('processing_status')}</th>
                  <th className="sortable" onClick={() => handleSort('assigned_to_processor_name')}>Processor{sortMark('assigned_to_processor_name')}</th>
                  <th className="sortable" onClick={() => handleSort('deposit_marked_at')}>Marked Deposit{sortMark('deposit_marked_at')}</th>
                  <th>Created By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((t) => {
                  const r = t as any;
                  const customer = `${t.first_name || ''} ${t.last_name || ''}`.trim() || 'Unknown';
                  return (
                    <tr key={t.id}>
                      <td>{customer}</td>
                      <td>${Number(t.amount || 0).toFixed(2)}</td>
                      <td>{fmtDateTime(t.created_at)}</td>
                      <td>{t.category || 'N/A'}</td>
                      <td>{t.sale_state || 'N/A'}</td>
                      <td>{(t.processing_status || 'PENDING_ASSIGNMENT').replace(/_/g, ' ')}</td>
                      <td>{r.assigned_to_processor_name || 'Unassigned'}</td>
                      <td>{fmtDateTime(r.deposit_marked_at)}</td>
                      <td>{r.created_by_name || 'Unknown'}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => setSelectedTransaction(t)} title="View Details">
                            📋 Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredTransactions.length === 0 && (
              <div className="no-data">
                <p>{transactions.length === 0 ? 'No sales are marked as Deposit yet.' : 'No deposits match the current filters.'}</p>
              </div>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalItems={filteredTransactions.length}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 20, 30, 50, 100]}
          />
        </div>

        <ProcessingDetailsPanel
          transaction={selectedTransaction}
          isOpen={!!selectedTransaction}
          onClose={handleCloseDetails}
          decryptedData={showEncryptedData === ((selectedTransaction as any)?.transaction_id || selectedTransaction?.id) ? decryptedData : null}
          onDecryptData={handleDecryptData}
          onEdited={loadDeposits}
        />
      </div>
    </DashboardLayout>
  );
};

export default Deposits;
